import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useReactFlow, XYPosition } from 'reactflow';
import cloneDeep from 'lodash/cloneDeep';
import { isNil } from 'lodash';
import { log } from '@/logger/logger.ts';
import { Node, Edge, BaseNodeData, PromptData } from '@/types/node';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { I18N_KEYS } from '@/language/keys';
import { SubscriptionType } from '@/types/shared';
import { useGlobalStore } from '@/state/global.state';
import { useWorkflowStore, useUserWorkflowRole } from '@/state/workflow.state';
import { usePromptEditorFocusStore } from '@/state/promptEditorFocus.state';
import { getCenterScreenPosition } from '@/hooks/useReactFlowViewportPositions';
import { useNodeDuplication } from '@/hooks/useNodeDuplication';
import { NodeType } from '@/enums/node-type.enum';
import { IMPORT_MODEL_NODE_ID, PROMPT_NODE_ID } from '@/consts/node-types.consts';
import { FF_BLOCK_FREE_USER_MODEL_IMPORT, FF_WEAVE_FIGMA_NODE } from '@/consts/featureFlags';
import { ADDED_NEW_NODE_EVENT } from '@/consts/analytics.consts';
import { useIsCustomModelApprovalEnabled } from '@/queries/useModelManagement';
import {
  isFigmaClipboardEnvironmentMismatch,
  parseFigmaSelectionFromClipboardHtml,
  type FigmaClipboardFrame,
} from '@/components/Nodes/FigmaNode/parseFigmaClipboardHtml';
import { FIGMA_NODE_EVENTS } from '@/components/Nodes/FigmaNode/figmaNode.analytics';
import { FIGMA_BASE_URL } from '@/services/figma';
import { prepareFigmaNodeDataForDuplicate } from '@/components/Nodes/FigmaNode/figmaNodeSnapshotCopy';
import { isValidModelImportIdentifier } from '@/utils/model-import.utils';
import { truncateStrIfNeeded } from '@/utils/strings.utils';
import { SubflowTemplateType } from '@/types/api/subflowTemplate';
import { calculateMultiNodeBoundsWithPadding } from '@/components/Nodes/utils/nodeUtils';
import { PROMPT_MAX_CHARS } from '../Nodes/consts/validations.consts';
import { useCopyNodesToClipboard } from './hooks/useCopyNodesToClipboard';
import { assetIdsIn, isCrossWorkflow, type NodeClipboardPayload } from './nodeClipboard';
import { usePendingAssetInsertion } from './hooks/usePendingAssetInsertion';
import { useNodeInsertionError } from './hooks/useNodeInsertionError';
import { DEFAULT_GROUP_PADDING } from './Views/NodeGrouping/groups.types';
import { AddNewNodeOptions, NodePlacement } from './flowTypes';
import { useFlowView, useNodes } from './FlowContext';
import { isPasteFromEditableElement } from './utils';
import { surfaceMultiConnectResult, trackMultiConnectResult } from './utils/multiConnectSnackbar';
import { nodeModelIdentifier, trackNodesAdded } from './nodeAdded.analytics';
import type { NodeDefinition } from '@/types/api/nodeDefinition';

const logger = log.getLogger('useGraph');

// TODO remove and use NodePlacement directly at call sites
function placementFromNewNodeArgs({
  dropX,
  dropY,
  fromCenter,
  absolutePosition,
}: Pick<AddNewNodeOptions, 'dropX' | 'dropY' | 'fromCenter' | 'absolutePosition'>): NodePlacement {
  if (typeof dropX === 'number' && typeof dropY === 'number') {
    if (fromCenter) {
      return { type: 'fromCenter', dx: dropX, dy: dropY };
    } else {
      return absolutePosition ? { type: 'absolute', x: dropX, y: dropY } : { type: 'drop', x: dropX, y: dropY };
    }
  } else {
    return { type: 'atCenter' };
  }
}

function getNewNodePosition(
  flowContainerRect: DOMRect,
  screenToFlowPosition: (p: XYPosition) => XYPosition,
  placement: NodePlacement,
): XYPosition {
  switch (placement.type) {
    case 'fromCenter': {
      const centerPosition = getCenterScreenPosition(flowContainerRect, screenToFlowPosition);
      return { x: centerPosition.x + placement.dx, y: centerPosition.y + placement.dy };
    }
    case 'absolute': {
      return { x: placement.x, y: placement.y };
    }
    case 'drop': {
      const flowPosition = screenToFlowPosition({
        x: placement.x,
        y: placement.y,
      });
      return flowPosition;
    }
    case 'atCenter': {
      return getCenterScreenPosition(flowContainerRect, screenToFlowPosition);
    }
    default: {
      const _exhaustiveCheck: never = placement;
      throw new Error('invalid node placement');
    }
  }
}

function createNodeFromTemplate(action: AddNewNodeOptions['action'], position: XYPosition): Node | undefined {
  const savedNode = action.savedTemplateData?.nodes[0];
  if (!savedNode) return undefined;
  const clone = cloneDeep(savedNode);
  const data = { ...clone.data, name: action.name ?? savedNode.data.name };
  return {
    ...clone,
    id: crypto.randomUUID(),
    position,
    parentId: undefined,
    locked: false,
    selected: false,
    createdAt: new Date().toISOString(),
    data: clone.type === NodeType.FigmaNode ? prepareFigmaNodeDataForDuplicate(data) : data,
  };
}

export function createNewNode(
  nodeTypeId: string,
  nodeTypes: NodeDefinition[],
  position: XYPosition,
  initialData?: any,
  pastedData?: any,
  options?: AddNewNodeOptions['options'],
) {
  const selectedOption = nodeTypes.find((option) => option.id === nodeTypeId);
  if (selectedOption) {
    // Create a new node object with the selected option's data
    const { metadata: _metadata, ...selectedOptionWithoutMetadata } = selectedOption;
    const newNode: Node = {
      ...selectedOptionWithoutMetadata,
      locked: selectedOptionWithoutMetadata.data.isLocked ?? false,
      parentId: undefined,
      id: crypto.randomUUID(),
      position,
      createdAt: new Date().toISOString(),
      data: {
        ...cloneDeep(selectedOptionWithoutMetadata.data),
        ...initialData,
        ...(initialData ? { initialData } : {}),
        ...(pastedData ? { pastedData } : {}),
      } as BaseNodeData, // FIXME
    };

    if (options?.isClearInitialData) {
      delete newNode.data.initialData;
    }

    // The Prompt node's definition still seeds the legacy default prompt, which arrives as real
    // content the user has to select and delete before writing their own (WEA-3455) — and which
    // hides the prompt suggested behind an empty editor. A new prompt starts empty unless the
    // caller brought its own text (pasted text, a variable turned into a prompt, a prompt dragged
    // out of node history).
    // Both prompt types, since the definition is still the v2 one that `migratePromptV2ToPromptV3`
    // upgrades on load.
    const isPromptNode = selectedOption.type === NodeType.Prompt || selectedOption.type === NodeType.PromptV3;

    if (isPromptNode && initialData?.prompt === undefined) {
      (newNode.data as PromptData).prompt = '';
    }

    return newNode;
  }
}

export function useGraph() {
  const { t } = useTranslation();
  const role = useUserWorkflowRole();
  const { screenToFlowPosition } = useReactFlow();
  const flowView = useFlowView();
  const { track } = useAnalytics();
  const { isSubscriptionTypeIn } = useSubscriptionPermissions();
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const getReactFlowContainerRect = useWorkflowStore((s) => s.getReactFlowContainerRect);
  const workflowId = useWorkflowStore((state) => state.recipe.id);
  const nodeTypes = useWorkflowStore((state) => state.nodeTypes);
  const isCustomModelApprovalEnabled = useIsCustomModelApprovalEnabled();
  const { value: isFigmaNodeEnabled } = useFeatureFlag(FF_WEAVE_FIGMA_NODE);
  const isBlockFreeUserModelImportEnabled = useFeatureFlagEnabled(FF_BLOCK_FREE_USER_MODEL_IMPORT);
  const startPendingInsertion = usePendingAssetInsertion();
  const reportInsertionError = useNodeInsertionError();

  const insertNewNode = useCallback(
    ({
      action,
      source = 'add_node',
      dropX,
      dropY,
      absolutePosition = false,
      fromCenter = false,
      connection,
      additionalConnections,
      shouldSelect = true,
      options,
    }: AddNewNodeOptions) => {
      const isSubflowTemplate =
        action.type === SubflowTemplateType.SavedNode ||
        action.type === SubflowTemplateType.SavedModel ||
        action.type === SubflowTemplateType.Subflow;
      const isMultiConnect = !!(connection && additionalConnections && additionalConnections.length > 0);
      // Reported once the node exists, so a failed lookup below isn't counted as an add.
      const addedNodeEventProperties = {
        type: action.displayName,
        definitionType: action.type || nodeTypes.find((n) => n.id === action.id)?.type,
        nodeDefinitionId: action.id,
        is_subflow_template: isSubflowTemplate,
        multi_connect: isMultiConnect,
        sibling_count: additionalConnections?.length ?? 0,
        source,
        workflowId,
        ...(isSubflowTemplate && {
          subflow_template_id: action.subflowTemplateId,
          subflow_template_type: action.type,
          is_owner: action.isOwner,
          insert_method: action.insertMethod,
        }),
      };
      const reactFlowContainerRect = getReactFlowContainerRect();
      if (reactFlowContainerRect === undefined) throw new Error('Expected Flow container');

      const placement = placementFromNewNodeArgs({ dropX, dropY, absolutePosition, fromCenter });
      const position = getNewNodePosition(reactFlowContainerRect, screenToFlowPosition, placement);

      // Subflow templates carry many nodes + edges, not a single node. They route to
      // a separate FlowView method that builds the wrapper, remaps ids, and reparents
      // children inside one undo step. The single-node path below is left untouched.
      if (action.type === SubflowTemplateType.Subflow && action.savedTemplateData) {
        flowView.insertTemplateGraph({ name: action.name ?? '', data: action.savedTemplateData }, position);
        track(ADDED_NEW_NODE_EVENT, addedNodeEventProperties, TrackTypeEnum.BI);
        return undefined;
      }

      const newNode = isSubflowTemplate
        ? createNodeFromTemplate(action, position)
        : createNewNode(action.id, nodeTypes, position, action.initialData, action.pastedData, options);
      if (newNode) {
        const result = flowView.addNewNode(newNode, connection, additionalConnections);
        track(
          ADDED_NEW_NODE_EVENT,
          { ...addedNodeEventProperties, model: nodeModelIdentifier(newNode) },
          TrackTypeEnum.BI,
        );
        if (shouldSelect) {
          flowView.selectedNodes = [newNode.id];
        }
        if (connection && additionalConnections && additionalConnections.length > 0) {
          surfaceMultiConnectResult(result, additionalConnections.length + 1, updateSnackbarData);
          trackMultiConnectResult({
            result,
            entry: 'drop_on_canvas',
            siblingCount: additionalConnections.length,
            track,
          });
        }
        if (
          (newNode.type === NodeType.Prompt || newNode.type === NodeType.PromptV3) &&
          action.initialData?.prompt === undefined
        ) {
          usePromptEditorFocusStore.getState().setPendingFocusNodeId(newNode.id);
        }
        return newNode;
      } else {
        logger.error(`Option for action "${action.name}" not found.`);
      }
    },
    [flowView, nodeTypes, getReactFlowContainerRect, screenToFlowPosition, track, updateSnackbarData, workflowId],
  );

  const addNewNode = useCallback(
    (options: AddNewNodeOptions) => {
      const { action } = options;
      if (!action.subflowTemplateId || !workflowId || assetIdsIn(action.savedTemplateData).length === 0) {
        return insertNewNode(options);
      }

      // Asset ids resolve only in the workflow owning them, so the template's are swapped before its nodes land.
      const source = { kind: 'subflow_template', id: action.subflowTemplateId } as const;
      const insertFailed = (error: unknown) => {
        reportInsertionError('template', error);
      };
      const rect = getReactFlowContainerRect();
      if (!rect || !action.savedTemplateData) return undefined;
      const position = getNewNodePosition(rect, screenToFlowPosition, placementFromNewNodeArgs(options));
      const isGroup = action.type === SubflowTemplateType.Subflow;
      const nodes = isGroup
        ? flowView
            .shiftNodesToTopLeft(action.savedTemplateData.nodes, position)
            .map((node) => ({ ...node, parentId: undefined }))
        : [createNodeFromTemplate(action, position)].filter((node): node is Node => !!node);
      startPendingInsertion(
        source,
        action.savedTemplateData,
        {
          nodes,
          ...(isGroup && {
            groupBounds: calculateMultiNodeBoundsWithPadding(nodes, DEFAULT_GROUP_PADDING),
            groupColor: action.savedTemplateData.groupMetadata?.color,
          }),
        },
        (savedTemplateData) =>
          insertNewNode({
            ...options,
            dropX: position.x,
            dropY: position.y,
            absolutePosition: true,
            fromCenter: false,
            action: { ...action, savedTemplateData },
          }),
        insertFailed,
      );
      return undefined;
    },
    [
      insertNewNode,
      workflowId,
      getReactFlowContainerRect,
      screenToFlowPosition,
      flowView,
      startPendingInsertion,
      reportInsertionError,
    ],
  );

  const { positionDuplicatedNodes, prepareNodesForPaste, positionPreparedNodes, scrollNodeIntoViewIfNeeded } =
    useNodeDuplication();

  const createPromptNode = useCallback(
    (
      pastedText: string,
      {
        type = 'prompt_pasted_text',
        source,
        dropX,
        dropY,
      }: Pick<AddNewNodeOptions, 'dropX' | 'dropY' | 'source'> & { type?: string } = {},
    ) => {
      const { value, isTruncated } = truncateStrIfNeeded(pastedText.trim(), PROMPT_MAX_CHARS);
      if (isTruncated) {
        updateSnackbarData({
          text: t(I18N_KEYS.RECIPE_MAIN.NODES.PROMPT_NODE_V3.TRUNCATED_WARNING, { max: PROMPT_MAX_CHARS }),
          severity: 'error',
          icon: null,
          isOpen: true,
        });
      }
      addNewNode({
        source,
        action: {
          id: PROMPT_NODE_ID,
          // We use initialData as we can simply override the prompt. We discard it immediately after creation.
          initialData: { prompt: value },
          type,
        },
        dropX,
        dropY,
        options: { isClearInitialData: true },
      });
    },
    [addNewNode, t, updateSnackbarData],
  );

  const handlePastedFigmaFrames = useCallback(
    (frames: FigmaClipboardFrame[]) => {
      track(FIGMA_NODE_EVENTS.pasted, { workflowId, frameCount: frames.length }, TrackTypeEnum.Product);
      // Match multi-file canvas drop: cascade down-right by 100px from viewport center.
      frames.forEach((frame, index) => {
        addNewNode({
          source: 'paste',
          action: {
            id: NodeType.FigmaNode,
            pastedData: { figmaNode: { sourceFileKey: frame.fileKey, sourceNodeId: frame.nodeId } },
          },
          dropX: index * 100,
          dropY: index * 100,
          fromCenter: true,
        });
      });
    },
    [addNewNode, track, workflowId],
  );

  const pasteNodesOrImageFromClipboard = useCallback(
    (event: ClipboardEvent) => {
      if (role !== 'editor' || isPasteFromEditableElement(event)) {
        return;
      }

      const clipboardData = event.clipboardData;
      if (!clipboardData) throw new Error('Expected browser paste event to always have clipboardData');

      // Check for image files first (including macOS screen captures)
      for (const item of clipboardData.items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            logger.info('paste image from clipboard', { file });
            addNewNode({
              source: 'paste',
              action: {
                type: 'import',
                pastedData: { imageFile: file },
                id: 'wkKkBSd0yrZGwbStnU6rClone',
              },
            });
          }
          return;
        }
      }

      if (isFigmaNodeEnabled) {
        const { frames, hasUnsupportedNodes, environment } = parseFigmaSelectionFromClipboardHtml(
          clipboardData.getData('text/html'),
        );

        if (frames.length > 0) {
          const mismatch = isFigmaClipboardEnvironmentMismatch(environment, FIGMA_BASE_URL);
          if (mismatch) {
            logger.info('paste figma frames rejected: environment mismatch', mismatch);
            updateSnackbarData({
              text: t(I18N_KEYS.RECIPE_MAIN.NODES.FIGMA_NODE.PASTE_ERROR_WRONG_ENVIRONMENT, {
                sourceHost: mismatch.sourceHost,
                expectedHost: mismatch.expectedHost,
              }),
              severity: 'error',
              icon: null,
              isOpen: true,
            });
            return;
          }
        }

        if (hasUnsupportedNodes) {
          updateSnackbarData({
            text: t(I18N_KEYS.RECIPE_MAIN.NODES.FIGMA_NODE.PASTE_ERROR_UNSUPPORTED_SELECTION),
            severity: 'error',
            icon: null,
            isOpen: true,
          });
          return;
        }

        if (frames.length > 0) {
          logger.info('paste figma frames from clipboard', { count: frames.length });
          handlePastedFigmaFrames(frames);
          return;
        }
      }

      const pastedText = clipboardData.getData('text/plain');
      if (!pastedText) return;

      // handle paste nodes and edges
      let parsedJson: NodeClipboardPayload | undefined;
      try {
        parsedJson = JSON.parse(pastedText) as NodeClipboardPayload;
      } catch (_e) {
        // Imported models are a paid-plan feature: Free-plan users (under the flag) don't get the
        // import-model node — the paste falls through to a normal prompt node instead.
        const isModelImportBlockedForFreeUser =
          isBlockFreeUserModelImportEnabled && isSubscriptionTypeIn([SubscriptionType.Free]);
        const isValidModelImportUrl = isValidModelImportIdentifier(pastedText) && !isModelImportBlockedForFreeUser;
        if (isValidModelImportUrl) {
          if (isSubscriptionTypeIn([SubscriptionType.Enterprise]) && !isCustomModelApprovalEnabled) {
            return logger.warn('Model import is not supported for enterprise users', { pastedText });
          }
          return addNewNode({
            source: 'paste',
            action: {
              id: IMPORT_MODEL_NODE_ID,
              // We use pastedData as we need it inside the node. We then discard it
              pastedData: { importedModelIdentifier: pastedText },
            },
          });
        }

        return createPromptNode(pastedText, { source: 'paste' });
      }

      if (isNil(parsedJson)) return;

      const clipboardNodes: Node[] | undefined = parsedJson.nodes;
      const clipboardEdges: Edge[] | undefined = parsedJson.edges;

      if (clipboardNodes && clipboardNodes.length > 0) {
        track('paste_nodes', { source: 'clipboard', format: 'json' });
        logger.info('paste nodes', { source: 'clipboard', format: 'json' });
        const pasteFailed = (error: unknown) => {
          reportInsertionError('paste', error);
        };
        const paste = (nodes: Node[], positionNodes = positionDuplicatedNodes) => {
          try {
            flowView.pasteAndPositionNodesAndEdges(
              nodes,
              clipboardEdges ?? [],
              positionNodes,
              scrollNodeIntoViewIfNeeded,
            );
          } catch (e) {
            pasteFailed(e);
            return;
          }
          trackNodesAdded(nodes, 'paste');
        };

        // Asset ids resolve only in the workflow owning them, so another workflow's are swapped before the paste.
        if (isCrossWorkflow(parsedJson.workflowId, workflowId) && assetIdsIn(clipboardNodes).length > 0) {
          const source = { kind: 'workflow', id: parsedJson.workflowId } as const;
          const nodes = prepareNodesForPaste(clipboardNodes);
          startPendingInsertion(
            source,
            nodes,
            { nodes },
            (copied) => paste(copied, positionPreparedNodes),
            pasteFailed,
          );
        } else {
          paste(clipboardNodes);
        }
      } else {
        // json string - create prompt node also
        track('paste_nodes', { source: 'clipboard', format: 'text' });
        logger.info('paste nodes', { source: 'clipboard', format: 'text' });
        return createPromptNode(pastedText, { source: 'paste' });
      }
    },
    [
      role,
      addNewNode,
      createPromptNode,
      handlePastedFigmaFrames,
      isFigmaNodeEnabled,
      isSubscriptionTypeIn,
      isCustomModelApprovalEnabled,
      isBlockFreeUserModelImportEnabled,
      track,
      flowView,
      positionDuplicatedNodes,
      prepareNodesForPaste,
      positionPreparedNodes,
      startPendingInsertion,
      reportInsertionError,
      scrollNodeIntoViewIfNeeded,
      t,
      updateSnackbarData,
      workflowId,
    ],
  );

  const nodes = useNodes();
  const copyNodes = useCopyNodesToClipboard();
  const copyNodesToClipboard = useCallback(() => {
    copyNodes(nodes.filter((n) => n.selected).map((n) => n.id));
  }, [copyNodes, nodes]);

  return {
    addNewNode,
    createPromptNode,
    copyNodesToClipboard,
    pasteNodesOrImageFromClipboard,
  };
}
